import React from 'react';

export interface TabBarItem {
  key: string;
  label: string;
  icon?: React.ReactNode;
  badge?: number | string;
}

interface TabBarProps {
  items: TabBarItem[];
  activeKey: string;
  onChange: (key: string) => void;
  trailing?: React.ReactNode;
  className?: string;
}

// Compact underline tab bar: single-line labels, active tab = primary underline + text
// color, counts shown as small muted badges. Scrolls horizontally on narrow widths
// instead of wrapping labels to two lines.
const TabBar: React.FC<TabBarProps> = ({ items, activeKey, onChange, trailing, className = '' }) => {
  return (
    <div className={`flex items-center border-b border-gray-200 dark:border-gray-700 mb-6 ${className}`}>
      <div
        className="flex items-center gap-1 overflow-x-auto flex-1 min-w-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
      >
        {items.map(item => {
          const isActive = item.key === activeKey;
          return (
            <button
              key={item.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(item.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors flex-shrink-0 ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-secondary dark:hover:text-dark-text hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {item.icon}
              {item.label}
              {item.badge !== undefined && item.badge !== null && (
                <span className={`text-xs rounded-full px-1.5 py-0.5 ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {trailing && <div className="flex-shrink-0 pb-1">{trailing}</div>}
    </div>
  );
};

export default TabBar;
